<template>
  <section
    ref="$block"
    class="osk-home-blocks-ad-product"
  >
    <div class="osk-wrapper">
      <h2
        v-if="data.title"
        class="osk-home-blocks-ad-product__title"
      >
        {{ data.title }}
      </h2>

      <div
        class="osk-home-blocks-ad-product__card"
      >
        <div class="osk-home-blocks-ad-product__card-img">
          <img
            :src="item.imgPath"
            alt="ad-product"
            class="osk-home-blocks-ad-product__card-image"
          >
        </div>

        <div class="osk-home-blocks-ad-product__card-content">
          <span class="osk-home-blocks-ad-product__card-brand">
            {{ item.brandName }}
          </span>

          <span class="osk-home-blocks-ad-product__card-category">
            {{ item.categoryName }}
          </span>

          <span
            v-if="text"
            class="osk-home-blocks-ad-product__card-text"
          >
            {{ text }}
          </span>

          <UikitButton
            class="osk-home-blocks-ad-product__card-button"
            :color="!isMounted || (isDesktop || laptopM) ? 'white' : undefined"
            outline
            :light-loading="isDesktop || laptopM"
            :size="!isMounted || isDesktop ? 's' : 'm'"
            :to="`/products/${item.id}`"
            :loading="loading"
            @click="handleClick"
          >
            {{ $t('common.moreDetails') }}
          </UikitButton>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { PrimaryContentDTO, PrimaryPageProductDTO } from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  data: PrimaryContentDTO
}>();

const $block = ref<ComponentPublicInstance>();

const { $addEvent } = useNuxtApp();
const { isMounted, isDesktop, laptopM } = useUiBreakpoints();

useScrollEvent($block, event);

const loading = ref(false);

const item = computed(() => (props.data.content ?? {}) as PrimaryPageProductDTO);

const text = computed(() => props.data.additionalData?.description);

const analytics = computed(() => ({
  chapter: props.data.contentType,
  chapter_index: props.data.chapterIndex,
  segment_id: props.data.segmentId,
  id_chapter: props.data.id,
}));

function event() {
  $addEvent(AnalyticsEvents.SwiperBlockView, analytics.value);
}

function handleClick() {
  loading.value = true;

  $addEvent(AnalyticsEvents.SwiperBlockClick, {
    ...analytics.value,
    action: 'details',
  });
}
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-home-blocks-ad-product {
  padding-bottom: 42px;

  @include media-query(lg-and-up) {
    padding-bottom: 82px;
  }

  .osk-wrapper {
    @include media-query(md-and-up) {
      padding: 0 16px;
    }
  }

  &__title {
    @include overflow-ellipsis;
    @include font-heading;
    padding: 0 0 22px;

    @include media-query(md-and-up) {
      text-transform: uppercase;
      text-align: center;
      padding: 0 0 32px;
      max-width: 1000px;
      margin: 0 auto;
    }
  }

  &__card {
    display: flex;
    flex-direction: column;

    @include media-query(md-and-up) {
      flex-direction: row;
      border-radius: 4px;
      overflow: hidden;
      height: 469px;
    }
  }

  &__card-img {
    @include image-bg-grey;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
    padding-top: 16px;
    border-radius: 2px;
    overflow: hidden;

    @include media-query(md-and-up) {
      width: 50%;
      height: 100%;
      padding-top: 0;
    }
  }

  &__card-image {
    display: block;
    max-width: 320px;
    width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;

    @include media-query(md-and-up) {
      width: auto;
      max-height: 380px;
      height: 100%;
    }
  }

  &__card-content {
    padding: 16px 0 0;
    display: flex;
    flex-direction: column;

    @include media-query(md-and-up) {
      padding: 0 60px;
      width: 50%;
      align-items: center;
      background: $black;
      color: $white;
      justify-content: center;
      text-align: center;
    }

    @include media-query(lg-and-up) {
      padding: 0 100px;
    }

    @include media-query(xl-and-up) {
      padding: 0 120px;
    }
  }

  &__card-brand {
    @include line-clamp;
    @include font-style($font-size-subheading, normal, $font-weight-bold);
    margin-bottom: 8px;

    @include media-query(md-and-up) {
      font-weight: 700;
      font-size: 32px;
      line-height: normal;
      margin-bottom: 38px;
      order: 2;
    }
  }

  &__card-category {
    margin-bottom: $indent-medium;
    color: $grey;

    @include media-query(md-and-up) {
      margin-bottom: $indent-small;
      order: 1;
      color: #fff;
    }
  }

  &__card-text {
    @include line-clamp(4);
    font-size: 14px;
    font-weight: 500;
    line-height: normal;
    margin-bottom: $indent-large;

    @include media-query(md-and-up) {
      @include line-clamp(3);
      @include font-style($font-size-caption, normal, $font-weight-medium);
      margin-bottom: 42px;
      order: 3;
    }
  }

  &__card-button {
    min-width: 147px;
    order: 4;
  }
}
</style>
